import React from "react";
import styled from "styled-components";
import dayjs from "dayjs";

import type { TaskDateChip, TaskPeriodFilter } from "./taskBoardUtils";

/** 日付チップの並びとラベル（先頭は解除 = すべて、以降は過去 → 未来の時系列順） */
const CHIP_OPTIONS: { value: TaskDateChip | null; label: string }[] = [
  { value: null, label: "すべて" },
  { value: "lastWeek", label: "先週" },
  { value: "today", label: "本日" },
  { value: "tomorrow", label: "明日" },
  { value: "thisWeek", label: "今週" },
];

/**
 * グループ枠。選択肢が互いに排他な「1 つのグループ」であることを、
 * MA オプションチップ（MaOptionSlotChip の Segmented）と同じ結合ボタンで表現する。
 * 色・角丸・フォントは FilterChip（pill・青アクセント・bodytext-13）に揃える。
 */
const Segmented = styled.div`
  display: inline-flex;
  width: fit-content;
  flex-shrink: 0;
  border: 1px solid var(--soremo-blue);
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
`;

const SegmentButton = styled.button.attrs({ className: "bodytext-13" })<{
  $active: boolean;
}>`
  appearance: none;
  border: none;
  margin: 0;
  padding: 4px 10px;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "var(--soremo-blue)" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "var(--soremo-blue)")};
  transition: background-color 0.15s, color 0.15s;

  & + & {
    border-left: 1px solid var(--soremo-blue);
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? "var(--soremo-deep-blue)" : "var(--soremo-blue)"};
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid var(--soremo-deep-blue);
    outline-offset: -2px;
  }
`;

/**
 * 月チップの選択肢（先月 / 今月 / 来月）。値は `YYYY-MM`。
 * 日付チップ（週・日単位）の隣に相対的な月単位を固定で並べる。
 * アクティビティ表示の既定（当月）は「今月」チップに一致する。
 */
const monthOptions = (): { month: string; label: string }[] => {
  const base = dayjs();
  return [
    { month: base.subtract(1, "month").format("YYYY-MM"), label: "先月" },
    { month: base.format("YYYY-MM"), label: "今月" },
    { month: base.add(1, "month").format("YYYY-MM"), label: "来月" },
  ];
};

interface PeriodFilterChipsProps {
  /** 選択中の期間。null = 絞り込みなし（すべて） */
  value: TaskPeriodFilter | null;
  /** 選択の変更。「すべて」を選ぶと null で呼ばれる */
  // eslint-disable-next-line no-unused-vars
  onChange: (next: TaskPeriodFilter | null) => void;
}

/**
 * タスクボード共通の期間フィルター
 * （すべて / 先週 / 本日 / 明日 / 今週 + 先月 / 今月 / 来月）。
 *
 * 日付チップと月チップをまたいだ単一選択で、「すべて」が解除（既定）を兼ねる。
 * 表・ガント・アクティビティ集計のどのビューでも同じ選択が効く。
 *
 * クリックでその期間に日付を持つタスク（アクティビティ表示では実績）へ
 * 絞り込む。完了フィルターの連動（先週 = 完了、今週 = 未完了、本日・明日 =
 * すべて、月 = すべて）は呼び出し側（CrossProjectTaskBoard）が行う。
 */
const PeriodFilterChips: React.FC<PeriodFilterChipsProps> = ({
  value,
  onChange,
}) => (
  <div
    className="u-row u-items-center u-gap8 u-wrap"
    role="group"
    aria-label="期間で絞り込み"
  >
    <Segmented role="group" aria-label="日付で絞り込み">
      {CHIP_OPTIONS.map((option) => {
        const active =
          option.value === null
            ? value === null
            : value?.kind === "chip" && value.chip === option.value;
        return (
          <SegmentButton
            key={option.label}
            type="button"
            aria-pressed={active}
            $active={active}
            onClick={() =>
              onChange(
                option.value === null
                  ? null
                  : { kind: "chip", chip: option.value },
              )
            }
          >
            {option.label}
          </SegmentButton>
        );
      })}
    </Segmented>
    <Segmented role="group" aria-label="月で絞り込み">
      {monthOptions().map(({ month, label }) => {
        const active = value?.kind === "month" && value.month === month;
        return (
          <SegmentButton
            key={label}
            type="button"
            aria-pressed={active}
            $active={active}
            onClick={() => onChange({ kind: "month", month })}
          >
            {label}
          </SegmentButton>
        );
      })}
    </Segmented>
  </div>
);

export default PeriodFilterChips;
